import type {
  JourneyPage,
  JourneyJson,
} from "@/components/quote/journey/quoteJourneyState";
import {
  WORKBENCH_FILTER_KEYS,
  type WorkbenchSort,
  type WorkbenchFilterKey,
} from "./quoteProjectView";
import {
  isCommercialJourneyView,
  isPartLinkJourneyView,
  type CommercialJourneyView,
  type PartLinkJourneyView,
} from "@/components/quote/worksheetJourneyView";
import type { CustomMaterialSpec } from "@/api/factorQuotes";
import {
  CUSTOM_MATERIAL_CLASSES,
  draftOf,
  parseDraft,
} from "@/components/quote/customMaterial";
export type ProjectBatchView = {
  process: { id: string; label: string } | null;
  material: { id: string; label: string } | null;
  customMaterial: CustomMaterialSpec | null;
  surfaces: { none: boolean; items: { id: string; label: string }[] };
  heat: { none: boolean; items: { id: string; label: string }[] };
  tolerance: "" | "coarse" | "standard" | "precision" | "ultra";
};
export type ProjectDrawingView = {
  pending: { documentId: string; filename: string }[];
  targets: Record<string, string>;
};
export function emptyProjectBatch(): ProjectBatchView {
  return {
    process: null,
    material: null,
    customMaterial: null,
    surfaces: { none: false, items: [] },
    heat: { none: false, items: [] },
    tolerance: "",
  };
}
export type ProjectStep = "checklist" | "workbench" | "summary";
export type ProjectParamsBaseline = {
  profit_ratio: number | null;
  lot_size: number | null;
  processing_mode: "labor_only" | "turnkey";
  tolerance: "coarse" | "standard" | "precision" | "ultra";
  onetime_mode: "per_order" | "threshold";
};
export type ProjectInputDraft =
  | { kind: "number"; raw: string; baseline: number | null }
  | {
      kind: "params";
      patch: Record<string, string>;
      numeric: { profit_ratio: string; lot_size: string };
      baseline: ProjectParamsBaseline;
    }
  | { kind: "prices"; values: Record<string, Record<string, string>> }
  | {
      kind: "fees";
      values: Record<string, string>;
      rush: string;
      baseline: { fees: Record<string, number>; rush_pct: number };
    };
export type ProjectJourneyView = {
  projectId: string;
  step: ProjectStep;
  checklistExpanded?: boolean;
  tiers: number[];
  tierText: string;
  withPure: boolean;
  sort: WorkbenchSort | null;
  filterKey: WorkbenchFilterKey | null;
  pickedLineIds: string[];
  openLine: { lineId: string; quoteId: string } | null;
  linkLineId: string | null;
  batch: ProjectBatchView | null;
  commercial: CommercialJourneyView | null;
  partLink: { lineId: string; view: PartLinkJourneyView } | null;
  drawings: ProjectDrawingView;
  cadPending: Record<string, string>;
  scrollTop: number;
  drafts: Record<ProjectStep, Record<string, ProjectInputDraft>>;
};
const uuid = (v: unknown): v is string =>
  typeof v === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const obj = (v: unknown): v is Record<string, unknown> =>
  v !== null &&
  typeof v === "object" &&
  !Array.isArray(v) &&
  Object.getPrototypeOf(v) === Object.prototype;
const keys = (v: Record<string, unknown>, allowed: string[]) =>
  Object.keys(v).every((k) => allowed.includes(k));
const raw = (v: unknown): v is string =>
  typeof v === "string" && v.length <= 100;
const textMap = (v: unknown) =>
  obj(v) && Object.entries(v).every(([k, x]) => k.length <= 200 && raw(x));
const numberMap = (v: unknown) =>
  obj(v) &&
  Object.entries(v).every(
    ([k, x]) => k.length <= 200 && typeof x === "number" && Number.isFinite(x),
  );
export function isProjectBatchView(v: unknown): v is ProjectBatchView {
  const choice = (x: unknown) =>
    x === null ||
    (obj(x) &&
      keys(x, ["id", "label"]) &&
      uuid(x.id) &&
      typeof x.label === "string" &&
      x.label.length <= 200);
  const finishing = (x: unknown) =>
    obj(x) &&
    keys(x, ["none", "items"]) &&
    typeof x.none === "boolean" &&
    Array.isArray(x.items) &&
    x.items.length <= 21 &&
    (!x.none || x.items.length === 0) &&
    x.items.every((item) => item !== null && choice(item)) &&
    new Set(x.items.map((item) => (item as { id: string }).id)).size ===
      x.items.length;
  if (
    !obj(v) ||
    !keys(v, [
      "process",
      "material",
      "customMaterial",
      "surfaces",
      "heat",
      "tolerance",
    ]) ||
    !choice(v.process) ||
    !choice(v.material) ||
    !finishing(v.surfaces) ||
    !finishing(v.heat) ||
    !["", "coarse", "standard", "precision", "ultra"].includes(
      String(v.tolerance),
    )
  )
    return false;
  const customMaterial = v.customMaterial;
  if (customMaterial !== null) {
    if (
      !obj(customMaterial) ||
      !keys(customMaterial, [
        "name",
        "material_class",
        "density_g_cm3",
        "unit_price_per_kg",
        "scrap_unit_price_per_kg",
      ]) ||
      typeof customMaterial.name !== "string" ||
      !CUSTOM_MATERIAL_CLASSES.some(
        (item) => item.value === customMaterial.material_class,
      ) ||
      ["density_g_cm3", "unit_price_per_kg", "scrap_unit_price_per_kg"].some(
        (key) =>
          customMaterial[key] !== undefined &&
          customMaterial[key] !== null &&
          (typeof customMaterial[key] !== "number" ||
            !Number.isFinite(customMaterial[key])),
      )
    )
      return false;
    if (
      "error" in
      parseDraft(draftOf(customMaterial as unknown as CustomMaterialSpec))
    )
      return false;
  }
  return !(v.material && v.customMaterial);
}
function validDraft(id: string, v: unknown): boolean {
  if (!obj(v)) return false;
  if (v.kind === "number")
    return (
      (id === "sets" ||
        (uuid(id.split(":")[0]) && /:(qty_per_set|drawing_page)$/.test(id))) &&
      keys(v, ["kind", "raw", "baseline"]) &&
      raw(v.raw) &&
      (v.baseline === null ||
        (typeof v.baseline === "number" && Number.isFinite(v.baseline)))
    );
  if (v.kind === "params")
    return (
      id === "params" &&
      keys(v, ["kind", "patch", "numeric", "baseline"]) &&
      obj(v.patch) &&
      Object.entries(v.patch).every(([k, x]) =>
        (
          ({
            processing_mode: ["labor_only", "turnkey"],
            tolerance: ["coarse", "standard", "precision", "ultra"],
            onetime_mode: ["per_order", "threshold"],
          }) as Record<string, string[]>
        )[k]?.includes(String(x)),
      ) &&
      obj(v.numeric) &&
      keys(v.numeric, ["profit_ratio", "lot_size"]) &&
      raw(v.numeric.profit_ratio) &&
      raw(v.numeric.lot_size) &&
      obj(v.baseline) &&
      keys(v.baseline, [
        "profit_ratio",
        "lot_size",
        "processing_mode",
        "tolerance",
        "onetime_mode",
      ]) &&
      [v.baseline.profit_ratio, v.baseline.lot_size].every(
        (x) => x === null || (typeof x === "number" && Number.isFinite(x)),
      ) &&
      ["labor_only", "turnkey"].includes(String(v.baseline.processing_mode)) &&
      ["coarse", "standard", "precision", "ultra"].includes(
        String(v.baseline.tolerance),
      ) &&
      ["per_order", "threshold"].includes(String(v.baseline.onetime_mode))
    );
  if (v.kind === "prices")
    return (
      id === "prices" &&
      keys(v, ["kind", "values"]) &&
      obj(v.values) &&
      Object.entries(v.values).every(
        ([cid, fields]) => uuid(cid) && textMap(fields),
      )
    );
  if (v.kind === "fees")
    return (
      id === "fees" &&
      keys(v, ["kind", "values", "rush", "baseline"]) &&
      textMap(v.values) &&
      raw(v.rush) &&
      obj(v.baseline) &&
      keys(v.baseline, ["fees", "rush_pct"]) &&
      numberMap(v.baseline.fees) &&
      typeof v.baseline.rush_pct === "number" &&
      Number.isFinite(v.baseline.rush_pct)
    );
  return false;
}
export function emptyProjectJourney(projectId: string): ProjectJourneyView {
  return {
    projectId,
    step: "checklist",
    tiers: [1],
    tierText: "1",
    withPure: false,
    sort: null,
    filterKey: null,
    pickedLineIds: [],
    openLine: null,
    linkLineId: null,
    batch: null,
    commercial: null,
    partLink: null,
    drawings: { pending: [], targets: {} },
    cadPending: {},
    scrollTop: 0,
    drafts: { checklist: {}, workbench: {}, summary: {} },
  };
}
export function projectJourneyData(
  view: ProjectJourneyView,
): Record<string, JourneyJson> {
  return structuredClone(view) as unknown as Record<string, JourneyJson>;
}
export function readProjectJourney(
  page: JourneyPage | null,
  projectId?: string,
): ProjectJourneyView | null {
  if (page?.key !== "project" || page.version !== 1 || page.files.length)
    return null;
  const v = page.data;
  if (
    !keys(v, [
      "projectId",
      "step",
      "checklistExpanded",
      "tiers",
      "tierText",
      "withPure",
      "sort",
      "filterKey",
      "pickedLineIds",
      "openLine",
      "linkLineId",
      "batch",
      "commercial",
      "partLink",
      "drawings",
      "cadPending",
      "scrollTop",
      "drafts",
    ]) ||
    !uuid(v.projectId) ||
    (projectId !== undefined && v.projectId !== projectId) ||
    !["checklist", "workbench", "summary"].includes(String(v.step)) ||
    (v.checklistExpanded !== undefined &&
      typeof v.checklistExpanded !== "boolean") ||
    !Array.isArray(v.tiers) ||
    v.tiers.length < 1 ||
    v.tiers.length > 4 ||
    !v.tiers.every(
      (x) => typeof x === "number" && Number.isSafeInteger(x) && x > 0,
    ) ||
    new Set(v.tiers).size !== v.tiers.length ||
    !raw(v.tierText) ||
    typeof v.withPure !== "boolean" ||
    typeof v.scrollTop !== "number" ||
    !Number.isFinite(v.scrollTop) ||
    v.scrollTop < 0 ||
    !Array.isArray(v.pickedLineIds) ||
    !v.pickedLineIds.every(uuid) ||
    new Set(v.pickedLineIds).size !== v.pickedLineIds.length ||
    !(v.linkLineId === null || uuid(v.linkLineId))
  )
    return null;
  if (
    !(v.batch === null || isProjectBatchView(v.batch)) ||
    !(
      v.commercial === null ||
      (isCommercialJourneyView(v.commercial) &&
        v.commercial.form.drawing_no === "" &&
        v.commercial.baseline.drawing_no === "")
    ) ||
    !(
      v.partLink === null ||
      (obj(v.partLink) &&
        keys(v.partLink, ["lineId", "view"]) &&
        uuid(v.partLink.lineId) &&
        v.partLink.lineId === v.linkLineId &&
        isPartLinkJourneyView(v.partLink.view))
    )
  )
    return null;
  if (
    v.openLine !== null &&
    !(
      obj(v.openLine) &&
      keys(v.openLine, ["lineId", "quoteId"]) &&
      uuid(v.openLine.lineId) &&
      uuid(v.openLine.quoteId)
    )
  )
    return null;
  if (
    v.sort !== null &&
    !(
      obj(v.sort) &&
      keys(v.sort, ["key", "dir"]) &&
      ["name", "quantity", "subtotal"].includes(String(v.sort.key)) &&
      ["asc", "desc"].includes(String(v.sort.dir))
    )
  )
    return null;
  if (
    v.filterKey !== null &&
    !(WORKBENCH_FILTER_KEYS as readonly string[]).includes(String(v.filterKey))
  )
    return null;
  const drafts = v.drafts;
  const drawings = v.drawings;
  if (
    !obj(drawings) ||
    !keys(drawings, ["pending", "targets"]) ||
    !Array.isArray(drawings.pending) ||
    !drawings.pending.every(
      (item) =>
        obj(item) &&
        keys(item, ["documentId", "filename"]) &&
        uuid(item.documentId) &&
        typeof item.filename === "string" &&
        item.filename.length <= 255,
    ) ||
    new Set(
      drawings.pending.map(
        (item) => (item as { documentId: string }).documentId,
      ),
    ).size !== drawings.pending.length ||
    !obj(drawings.targets) ||
    !Object.entries(drawings.targets).every(
      ([documentId, lineId]) =>
        uuid(documentId) &&
        uuid(lineId) &&
        (drawings.pending as { documentId: string }[]).some(
          (item) => item.documentId === documentId,
        ),
    ) ||
    !obj(v.cadPending) ||
    !Object.entries(v.cadPending).every(
      ([lineId, documentId]) => uuid(lineId) && uuid(documentId),
    )
  )
    return null;
  if (
    !obj(drafts) ||
    !keys(drafts, ["checklist", "workbench", "summary"]) ||
    !["checklist", "workbench", "summary"].every(
      (step) =>
        obj(drafts[step]) &&
        Object.entries(drafts[step]).every(([id, draft]) =>
          validDraft(id, draft),
        ),
    )
  )
    return null;
  return v as unknown as ProjectJourneyView;
}
